import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

// Routing
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

// Store
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';

import { useFormik } from 'formik';

// UI
import { useStyles } from './style';
import {
  BaseButton,
  BaseCheckboxWithLabel,
  BaseDialog,
  BaseDivider,
  BaseTypography,
  FormikField,
} from 'src/components/common';
import { Box, Grid } from '@material-ui/core';
import { SearchTextEvents } from 'src/components/common/BaseTextField/SearchTextEvents';
import { BaseSunEditor } from 'src/components/common/BaseSunEditor';
import { FilesUploader } from 'src/components/common/FilesUploader';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import noUserPicture from 'src/assets/images/no-user-picture.svg';

// Todo: resolve ts-ignore
// @ts-ignore
import dateFormat from 'dateformat';
import {
  getArrWithNewEl,
  debounce,
  isEditingEventsForm,
} from 'src/utils/helpers';
import { useFetch } from 'src/utils/hooks/useFetch';
import { EventsApi } from 'src/api/base/Events';
import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import { IBaseForm } from 'src/utils/types/forms';
import { IEventsResponse } from '../../../../api/base/Events/type';
import ImageOrVideo from '../../../../components/common/ImageOrVideo/ImageOrVideo';

type PEventsContactForm = IBaseForm<any> & {
  initialValues?: IEventsResponse;
  shouldResetAfterSuccess?: boolean;
  eventsTemplate?: {
    created_at: string;
    id: number;
    link: string;
    updated_at: string;
    onSearch: (value: string) => void;
  };
};

const formInitialValues = {
  heading: '',
  header_image: '',
  description: '',
  start_date: '',
  end_date: '',
  all_day: '',
  media: '',
  content: [],
  location: '',
  signups_url: '',
};

const formKeys = Object.keys(formInitialValues);

export const EventsEventsForm: React.FC<PEventsContactForm> = (props) => {
  const { onSubmit, loading, errors, eventsTemplate, initialValues } = props;

  const history = useHistory();
  const classes = useStyles();

  const [contentField, setContentField] = useState<number[]>([]);
  const [contentFilter, setContentFilter] = useState('');

  const isEditForm = isEditingEventsForm(history.location.pathname);

  const validationSchema = Yup.object().shape({
    start_date: Yup.string().required('Required'),
    end_date: Yup.string().required('Required'),
    heading: Yup.string().required('Required'),
    header_image: Yup.string().required('Required'),
    description: Yup.string().required('Required'),
    location: Yup.string().required('Required'),
    signups_url: Yup.string().required('Required'),
  });

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    initialValues: initialValues
      ? { ...initialValues }
      : { ...formInitialValues },
    onSubmit: (values) => {
      const startDateFormated = dateFormat(values.start_date, 'isoDateTime');
      const endDateFormated = dateFormat(values.end_date, 'isoDateTime');
      const formData = new FormData();
      formData.set('heading', values.heading);
      formData.append('header_image', values.header_image);
      formData.set('location', values.location);
      formData.set('description', values.description);
      formData.set('start_date', startDateFormated);
      formData.set('end_date', endDateFormated);
      formData.set('all_day', values.all_day);
      for (let i = 0; i < values?.media?.length; i += 1) {
        formData.append('media[]', values?.media[i]);
      }
      formData.set('content', JSON.stringify(contentField));
      formData.set('signups_url', values.signups_url);

      onSubmit(formData);
    },
  });

  const { setFieldError, setFieldValue } = formik;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const handleOpenModal = () => {
    setIsOpenModal(true);
  };
  const handleOpen = () => {
    setIsOpen(true);
  };
  const handleCloseModal = () => {
    setIsOpenModal(false);
  };
  const handleClose = () => {
    setIsOpen(false);
    if (contentFilter) {
      setContentFilter('');
    }
  };
  useFormikErrorsHandler({ errors, setFieldError });
  const [{ response, isLoading, error }, doFetch] = useFetch(
    EventsApi.getAllEvents,
  );
  const { id } = useParams();
  useEffect(() => {
    doFetch(id);
  }, [id]);

  const debouncedSearchHandler = debounce((value: string) => {
    setContentFilter(value.toLowerCase());
  }, 500);

  const handleChangeContent = (contentId: number) => {
    const newArr = getArrWithNewEl(contentField, contentId);
    setContentField(newArr);
  };

  useEffect(() => {
    if (eventsTemplate) {
      formKeys.forEach((key) => {
        // @ts-ignore
        setFieldValue(key, eventsTemplate[key]);
      });
    }
  }, [setFieldValue, eventsTemplate]);

  const handleChooseFile = (eventFiles: (File | string)[]) => {
    const file = eventFiles && eventFiles[0];

    if (file) {
      formik.setFieldValue('header_image', file);
    }
  };

  const { user } = useSelector((state: RootState) => state.auth);

  const formattedInitialValueContent =
    initialValues?.content?.map((item) => item.id) || [];

  return (
    <div className={classes.root}>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.formGroups}>
          <div className={classes.formGroup}>
            <BaseTypography variant="h4">
              {initialValues ? 'View & Edit' : 'Create news'}
            </BaseTypography>
            <FormikField
              label="Heading"
              name="heading"
              formik={formik}
              placeholder="Add a heading here"
            />
            <div>
              <BaseTypography variant="caption" style={{ fontWeight: 500 }}>
                Upload file
              </BaseTypography>
              <BaseTypography variant="caption" className={classes.gray}>
                We recommend keep image to at least 1000 pixels wide and 16:9
                aspect ratio. Accepts .jpeg, .jpg, .png, .gif, .mp4 files only.
                Up to 20MB.
              </BaseTypography>
            </div>

            <FilesUploader
              onChooseFile={handleChooseFile}
              accept="image/*,video/*"
              multiple={false}
              error={formik.errors.header_image}
              helperText={formik.errors.header_image}
              files={formik.values.header_image}
              onDelete={() => formik.setFieldValue('header_image', undefined)}
            />

            <div className={classes.formRowN}>
              <BaseSunEditor
                formikError={formik.errors.description}
                defaultValue={formik.values.description}
                name="description"
                onChange={(value) => {
                  formik.setFieldValue('description', value);
                }}
              />
            </div>
            <div className={classes.dateRow}>
              <div className={classes.dateN}>
                Start time
                <BaseDateTimePicker
                  errorText={formik.errors.start_date as string}
                  error={Boolean(formik.errors.start_date)}
                  placeholder="Select date and time"
                  value={formik.values.start_date}
                  onChange={(value: any) => {
                    formik.setFieldValue('start_date', value);
                  }}
                  disablePast
                />
              </div>
              <div className={classes.dateN}>
                End time
                <BaseDateTimePicker
                  errorText={formik.errors.end_date as string}
                  error={Boolean(formik.errors.end_date)}
                  placeholder="Select date and time"
                  value={formik.values.end_date}
                  onChange={(value: any) => {
                    formik.setFieldValue('end_date', value);
                  }}
                  disablePast
                />
              </div>
            </div>
            <BaseCheckboxWithLabel
              label="All day"
              checked={formik.values.all_day}
              onChange={() => {
                formik.setFieldValue('all_day', !formik.values.all_day);
              }}
            />
            <FormikField
              label="Location"
              name="location"
              formik={formik}
              placeholder="Add a location"
            />
            <div>
              <BaseTypography>Related media</BaseTypography>
              <BaseDialog
                open={isOpenModal}
                onClose={handleCloseModal}
                title="Related media"
                paperStyles={{ maxWidth: '600px', width: '100%' }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">
                <Box className={classes.modalBoxSizing}>
                  <FilesUploader
                    onChooseFile={(acceptedFiles) => {
                      setFieldValue('media', acceptedFiles);
                    }}
                    files={formik.values.media}
                    accept="image/*,video/*"
                  />
                  <form className={classes.modal}>
                    <BaseButton
                      variant="contained"
                      onClick={handleCloseModal}
                      className={classes.btnCancel}
                      fullWidth>
                      Done
                    </BaseButton>
                  </form>
                </Box>
              </BaseDialog>
              <BaseButton variant="outlined" onClick={handleOpenModal}>
                Add related media
              </BaseButton>
            </div>

            <BaseDivider mTop="0px" mBottom="0px" />

            <div>
              <BaseTypography variant="caption" style={{ fontWeight: 500 }}>
                Related content
              </BaseTypography>
              <BaseTypography
                variant="caption"
                className={classes.gray}
                style={{ margin: '5px 0' }}>
                Connect related content from your newsroom to give readers more
                to explore
              </BaseTypography>

              <BaseButton variant="outlined" onClick={handleOpen}>
                Add related content
              </BaseButton>
              {error && <div>error</div>}
              {isLoading && <div>loading...</div>}
            </div>

            <BaseDivider mTop="0px" mBottom="0px" />

            <div>
              <div className={classes.FW}>Related contacts</div>
              <img
                className={classes.image}
                src={user?.avatar || noUserPicture}
                alt="avatar"
              />

              <div className={classes.FW}>
                {user?.profile.first_name} {user?.profile.last_name}
              </div>
              <div className={classes.gray}>Contacts book manager</div>
            </div>

            <BaseDivider mTop="0px" mBottom="0px" />

            <div className={classes.date}>
              <div className={classes.dateN}>
                <FormikField
                  label="Links"
                  name="signups_url"
                  formik={formik}
                  placeholder="Enter URL"
                />
              </div>
            </div>
          </div>
          <div className={classes.submitSection}>
            <BaseButton variant="contained" type="submit" loading={loading}>
              {isEditForm || initialValues ? ' Save changes' : 'Publish'}
            </BaseButton>
          </div>
        </div>
      </form>

      <BaseDialog
        open={isOpen}
        onClose={handleClose}
        title="Related content"
        paperStyles={{ maxWidth: '600px', width: '100%' }}>
        <Box className={classes.modalBoxSizingRelatedMedia}>
          <SearchTextEvents
            placeholder="Find content"
            onChange={(e: any) => {
              debouncedSearchHandler(e.target.value);
            }}
            style={{ width: '100%' }}
          />
          <br />

          <Grid container spacing={3}>
            {response?.data?.data
              .filter((data: any) =>
                data.heading.toLowerCase().includes(contentFilter),
              )
              .map((item: any) => {
                return (
                  <Grid item className={classes.related_mediaBox}>
                    <ImageOrVideo
                      controls={false}
                      className={classes.mediaImage}
                      src={item.header_image ? item.header_image : null}
                      width="50px"
                      alt="img"
                    />
                    <BaseTypography>{item.heading}</BaseTypography>
                    <BaseCheckboxWithLabel
                      className={classes.mediaCheckbox}
                      value={item.id}
                      key={item.id}
                      checked={
                        contentField.includes(item.id) ||
                        formattedInitialValueContent.includes(item.id)
                      }
                      onChange={() => handleChangeContent(item.id)}
                    />
                  </Grid>
                );
              })}
          </Grid>

          <BaseButton
            type="submit"
            variant="contained"
            fullWidth
            onClick={handleClose}
            style={{ marginTop: '10px' }}>
            Done
          </BaseButton>
        </Box>
      </BaseDialog>
    </div>
  );
};
